import { ComponentProps } from "react";
import { BlockTitle } from "@blocks/Blocks";
import Card from "@particles/card/Card";
import { CardVariations } from "@particles/card/utils/types";
import { cn } from "@util/helpers";

type ViewsStoriesProps = {
  /** The list of card props to be displayed. */
  cardProps: ComponentProps<typeof Card>[];
  /** Optional additional styling for the component. */
  className?: string;
};

/**
 * ViewsStories component to display the views stories for Singapore Budget page.
 * Figma: https://www.figma.com/design/FZiuxiCLQaNXAJmLRtAAY9/BUDGET-2025--WEB---APP---NL-?node-id=8518-386983&t=PUAyvdavKczeZd7x-4
 */
const ViewsStories = ({ cardProps, className }: ViewsStoriesProps) => {
  const mainVariation: CardVariations = {
    title: {
      color: "text-black",
      size: "text-4xl",
      extraClass: cn("font-bold font-lct lg:text-center text-left"),
    },
    content: {
      width: "w-full",
      extraClass: "text-left lg:text-center",
      blurb: {
        extraClass: "text-gray-850 text-lg lg:text-center pb-6 max-lg:block",
        wrapperClassName: cn("lg:my-2"),
      },
    },
    byline: {
      extraClass: cn("justify-start lg:justify-center text-xs"),
    },
  };

  const otherVariation: CardVariations = {
    title: {
      color: "text-black",
      size: "text-lg",
      extraClass: cn("font-bold font-lct"),
    },
    content: {
      width: "w-full",
      blurb: {
        extraClass: "text-gray-850 text-base pb-6 max-lg:block",
        wrapperClassName: cn("lg:my-2"),
      },
    },
    byline: {
      extraClass: cn("text-xs"),
    },
  };

  return (
    <div
      data-testid="singapore-budget-views-stories-component"
      className={className}
    >
      <BlockTitle text="VIEWS" rootClassName="text-center" />

      <div className={cn("mb-10 grid lg:grid-cols-2")}>
        {cardProps.map((card, index) => {
          return (
            <div
              className={cn("col-span-1 border-b border-gray-175 py-4", {
                "lg:col-span-2": index === 0,
                "lg:border-0": index === 1 || index === 2,
              })}
              key={`budget-views-stories-${card.id}`}
            >
              <Card
                {...card}
                variations={index === 0 ? mainVariation : otherVariation}
                className={cn("h-full border-gray-175", {
                  "lg:border-r lg:pr-3": [1].includes(index),
                  "lg:pl-3": [2].includes(index),
                })}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ViewsStories;
